import React from "react";
import type { Appointment } from "@myapp/contract";
import { useNavigate } from "react-router-dom";
import { FormSection } from "../../../../shared/ui/controls/FormSection/FormSection";
import { getAppointmentDeleteDialogOptions, useDeleteAppointment } from "src/entities/appointment";
import DeleteItemDialog from "../../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { useDeleteDialog } from "../../../../shared/hooks/useDeleteDialog";
import { useIsMobile } from "../../../../shared/hooks/useIsMobile";
import { useCanDeleteWorkItems } from "../../../../shared/hooks/useUserRoles";
import { routes } from "../../../../shared/config/routes";
import { AppointmentsTable } from "./AppointmentsTable";
import { AppointmentCardList } from "./AppointmentCardList";

interface Props {
  appointments: Appointment[];
  /** The Auftrag's Gewerk — see `AppointmentListLayoutProps`. */
  gewerkText?: string | null;
  /** Opens the upsert modal for a row. Rows are inert when omitted. */
  onEdit?: (appointmentId: number) => void;
}

export const AppointmentsSection: React.FC<Props> = ({ appointments, gewerkText, onEdit }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // One controller and one dialog for the whole list — a row only says which Termin it is about.
  const deleteDialog = useDeleteDialog();
  const deleteAppointment = useDeleteAppointment();
  const canDelete = useCanDeleteWorkItems();

  const askDelete = (a: Appointment) =>
    deleteDialog.openDelete(
      "Termin wirklich löschen?",
      async ({ forFullSeries }) => {
        await deleteAppointment.mutateAsync({
          id: a.id,
          forFullSeries,
          // Invalidates this Auftrag's audit feed, which sits on the same page.
          workOrderId: a.workOrderId,
        });
      },
      // A completed or begun Termin is deletable too — the dialog then warns, and for a series
      // asks which of the two deletions was meant.
      getAppointmentDeleteDialogOptions(a),
    );

  const handleReschedule = (a: Appointment) =>
    navigate(routes.disposition.rescheduleAppointment(a.id, a.schedule.plannedWorkStartsAt));

  // Swapped in JS rather than shown and hidden with CSS: a second copy of every row would answer
  // to the same labels, and the table alone is ten columns of duplicated markup.
  const Layout = isMobile ? AppointmentCardList : AppointmentsTable;

  return (
    <>
      <FormSection title="Termine" cardClassName="p-0 overflow-hidden " className="w-full">
        {appointments.length === 0 ? (
          <p className="p-[30px_25px] text-gray-400 text-sm">Keine Termine vorhanden.</p>
        ) : (
          <Layout
            appointments={appointments}
            gewerkText={gewerkText}
            onEdit={onEdit}
            onReschedule={handleReschedule}
            onDelete={canDelete ? askDelete : undefined}
          />
        )}
      </FormSection>

      {/* Outside the card: it is `overflow-hidden`, and a dialog is nobody's table cell. */}
      <DeleteItemDialog
        deleteModal={deleteDialog}
        singleLabel="Nur diesen Termin"
        seriesLabel="Diesen und alle folgenden"
      />
    </>
  );
};
